import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { useLoginPrompt } from '../../contexts/LoginPromptContext';
import { 
  getFullUserProfile, 
  getUserFollowers, 
  getUserFollowing, 
  getMyFollowingIds,
  followUser, 
  unfollowUser 
} from '../../services/supabaseService';

export const UserConnections: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();
  const { openLoginPrompt } = useLoginPrompt();

  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'followers' | 'following'>(
    tabParam === 'seguindo' ? 'following' : 'followers'
  );

  const [profileUser, setProfileUser] = useState<any>(null);
  const [followers, setFollowers] = useState<any[]>([]);
  const [following, setFollowing] = useState<any[]>([]);
  const [myFollowingIds, setMyFollowingIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  // Sync tab with URL search parameter
  useEffect(() => {
    if (tabParam === 'seguindo' && activeTab !== 'following') {
      setActiveTab('following');
    } else if (tabParam === 'seguidores' && activeTab !== 'followers') {
      setActiveTab('followers');
    }
  }, [tabParam]);

  const handleTabChange = (tab: 'followers' | 'following') => {
    setActiveTab(tab);
    setSearchParams({ tab: tab === 'following' ? 'seguindo' : 'seguidores' }, { replace: true });
  };

  // Determine target identifier (from param or current user)
  const targetIdentifier = userId || currentUser?.username || (currentUser?.id ? String(currentUser.id) : null);
  const isOwnProfile = !userId || (currentUser && (
    userId === String(currentUser.id) || 
    userId === currentUser.username || 
    userId === currentUser.email_copy || 
    userId === currentUser.auth_id
  ));

  // Load profile user and both connections lists
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!targetIdentifier) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [profileRes, followersList, followingList, myFollowSet] = await Promise.all([
          getFullUserProfile(targetIdentifier, currentUser?.id),
          getUserFollowers(targetIdentifier),
          getUserFollowing(targetIdentifier),
          currentUser?.id ? getMyFollowingIds(currentUser.id) : Promise.resolve(new Set<number>())
        ]);

        if (isMounted) {
          if (profileRes?.user) {
            setProfileUser(profileRes.user);
          }
          setFollowers(followersList || []);
          setFollowing(followingList || []);
          setMyFollowingIds(myFollowSet || new Set());
        }
      } catch (err) {
        console.error('Error loading connections data:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [targetIdentifier, currentUser?.id]);

  // Handle follow / unfollow toggle for users in list
  const handleFollowToggle = async (e: React.MouseEvent, targetUser: any) => {
    e.stopPropagation();

    if (!currentUser?.id) {
      openLoginPrompt('seguir outros jogadores');
      return;
    }

    const tId = Number(targetUser.id);
    const username = targetUser.username;
    if (!tId || !username) return;

    const isCurrentlyFollowing = myFollowingIds.has(tId);
    setActionLoadingId(tId);

    // Optimistic update
    setMyFollowingIds(prev => {
      const next = new Set(prev);
      if (isCurrentlyFollowing) {
        next.delete(tId);
      } else {
        next.add(tId);
      }
      return next;
    });

    try {
      if (isCurrentlyFollowing) {
        await unfollowUser(username, currentUser.id);
        showNotification(`Você deixou de seguir @${username}.`, 'info');
      } else {
        await followUser(username, currentUser.id);
        showNotification(`Você agora está seguindo @${username}!`, 'success');
      }
    } catch (err) {
      // Revert optimistic update
      setMyFollowingIds(prev => {
        const next = new Set(prev);
        if (isCurrentlyFollowing) {
          next.add(tId);
        } else {
          next.delete(tId);
        }
        return next;
      });
      showNotification('Erro ao atualizar status de seguidor.', 'error');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Filter list by search term
  const currentList = activeTab === 'followers' ? followers : following;
  const filteredList = useMemo(() => {
    if (!searchTerm.trim()) return currentList;
    const term = searchTerm.toLowerCase();
    return currentList.filter(u => 
      (u.fullname && u.fullname.toLowerCase().includes(term)) ||
      (u.username && u.username.toLowerCase().includes(term)) ||
      (u.bio && u.bio.toLowerCase().includes(term))
    );
  }, [currentList, searchTerm]);

  const backUrl = isOwnProfile ? '/perfil' : `/perfil/${profileUser?.username || userId}`;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-8 text-slate-100 font-sans">
      {/* Top Bar: Back Link */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(backUrl)}
          className="text-xs font-bold text-slate-400 hover:text-white flex items-center gap-2 transition-colors cursor-pointer group"
        >
          <i className="fas fa-arrow-left text-xs group-hover:-translate-x-1 transition-transform"></i>
          <span>Voltar ao Perfil {profileUser?.fullname ? `de ${profileUser.fullname}` : ''}</span>
        </button>
      </div>

      {/* Header Profile Summary */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-5 sm:p-7 flex flex-col sm:flex-row items-center sm:items-start gap-5 shadow-xl relative overflow-hidden">
        <div className="relative shrink-0">
          <img 
            src={profileUser?.avatar || profileUser?.photoURL || "https://i.pravatar.cc/150?u=cardumy"} 
            alt={profileUser?.username || "Perfil"} 
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-purple-500/40 object-cover bg-slate-950 shadow-md"
          />
          {profileUser?.store_logo && (
            <img 
              src={profileUser.store_logo} 
              alt="Logo da Loja" 
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border border-slate-900 object-cover bg-slate-950" 
              title="Lojista Parceiro" 
            />
          )}
        </div>

        <div className="flex-1 text-center sm:text-left min-w-0 space-y-1">
          <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight truncate">
              {profileUser?.fullname || profileUser?.username || (isOwnProfile ? 'Meu Perfil' : 'Perfil')}
            </h1>
            <i className="fas fa-circle-check text-blue-400 text-sm" title="Verificado"></i>
          </div>
          <p className="text-xs sm:text-sm text-purple-400 font-semibold">
            @{profileUser?.username || 'usuario'}
          </p>
          <p className="text-xs text-slate-400 pt-0.5 line-clamp-2">
            {profileUser?.bio || 'Membro da comunidade Cardumy TCG'}
          </p>
        </div>

        {/* View Profile Button */}
        <Link
          to={backUrl}
          className="shrink-0 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-xl text-xs font-bold transition-all border border-slate-700 flex items-center gap-1.5"
        >
          <span>Ver Perfil</span>
          <i className="fas fa-arrow-right text-[10px]"></i>
        </Link>
      </div>

      {/* Tabs & Search Controls */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-3">
          {/* Navigation Tabs */}
          <div className="flex items-center space-x-2 bg-slate-900/80 p-1.5 rounded-2xl border border-slate-800 w-full sm:w-auto">
            <button
              onClick={() => handleTabChange('followers')}
              className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer ${
                activeTab === 'followers'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
            >
              <i className="fas fa-users text-xs"></i>
              <span>Seguidores</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                activeTab === 'followers' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'
              }`}>
                {followers.length}
              </span>
            </button>

            <button
              onClick={() => handleTabChange('following')}
              className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer ${
                activeTab === 'following'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
            >
              <i className="fas fa-user-plus text-xs"></i>
              <span>Seguindo</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                activeTab === 'following' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'
              }`}>
                {following.length}
              </span>
            </button>
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-72">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
            <input 
              type="text" 
              placeholder={`Buscar em ${activeTab === 'followers' ? 'seguidores' : 'seguindo'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs cursor-pointer"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-slate-900/50 border border-slate-800/80 rounded-2xl p-4 flex items-center justify-between animate-pulse">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-full bg-slate-800"></div>
                <div className="space-y-2">
                  <div className="w-32 h-3.5 bg-slate-800 rounded"></div>
                  <div className="w-20 h-2.5 bg-slate-800/60 rounded"></div>
                </div>
              </div>
              <div className="w-20 h-8 bg-slate-800 rounded-xl"></div>
            </div>
          ))
        ) : filteredList.length > 0 ? (
          filteredList.map((userItem) => {
            const isMe = currentUser && (
              Number(currentUser.id) === Number(userItem.id) || 
              currentUser.username === userItem.username
            );
            const isFollowingUser = myFollowingIds.has(Number(userItem.id));
            const isActionLoading = actionLoadingId === Number(userItem.id);

            return (
              <div 
                key={userItem.id || userItem.username}
                onClick={() => navigate(`/perfil/${userItem.username}`)}
                className="group bg-slate-900/60 hover:bg-slate-900/90 border border-slate-800/80 hover:border-purple-500/40 rounded-2xl p-4 transition-all duration-200 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md"
              >
                {/* User Info */}
                <div className="flex items-center gap-3.5 min-w-0">
                  <div className="relative shrink-0">
                    <img 
                      src={userItem.avatar || "https://i.pravatar.cc/150?u=" + userItem.username} 
                      alt={userItem.username} 
                      className="w-12 h-12 rounded-full object-cover border border-slate-800 bg-slate-950"
                    />
                    {userItem.store_logo && (
                      <img 
                        src={userItem.store_logo} 
                        alt="Logo" 
                        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border border-slate-900 object-cover bg-slate-950"
                        title="Lojista Parceiro"
                      />
                    )}
                  </div>

                  <div className="min-w-0 space-y-0.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className="font-bold text-sm text-white group-hover:text-purple-300 transition-colors truncate">
                        {userItem.fullname || userItem.username}
                      </h3>
                      {userItem.role_id === 6 && (
                        <span className="px-1.5 py-0.2 bg-purple-500/20 text-purple-300 text-[8px] font-black uppercase rounded tracking-wider border border-purple-500/30">
                          Loja
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-medium truncate">
                      @{userItem.username}
                    </p>
                    {userItem.bio && (
                      <p className="text-[11px] text-slate-400 line-clamp-1 pt-0.5 max-w-md">
                        {userItem.bio}
                      </p>
                    )}
                  </div>
                </div>

                {/* Follow Button / Action */}
                <div className="flex items-center justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800/60 shrink-0">
                  {!isMe && (
                    <button
                      type="button"
                      onClick={(e) => handleFollowToggle(e, userItem)}
                      disabled={isActionLoading}
                      className={`px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 flex items-center gap-1.5 cursor-pointer ${
                        isFollowingUser
                          ? 'bg-slate-800 hover:bg-red-500/10 hover:border-red-500/40 text-slate-300 hover:text-red-400 border border-slate-700'
                          : 'bg-purple-600 hover:bg-purple-500 text-white shadow-md shadow-purple-600/20'
                      }`}
                    >
                      {isActionLoading ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ) : isFollowingUser ? (
                        <>
                          <i className="fas fa-check text-[10px]"></i>
                          <span>Seguindo</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-plus text-[10px]"></i>
                          <span>Seguir</span>
                        </>
                      )}
                    </button>
                  )}

                  <div className="w-8 h-8 rounded-xl bg-slate-800/50 group-hover:bg-purple-600/20 group-hover:text-purple-300 flex items-center justify-center text-slate-500 transition-colors">
                    <i className="fas fa-chevron-right text-[10px]"></i>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          /* Empty State */
          <div className="bg-slate-900/40 border border-slate-800/80 rounded-3xl p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-slate-800/50 border border-slate-700/60 rounded-2xl flex items-center justify-center mx-auto text-slate-500 text-2xl">
              <i className={`fas ${activeTab === 'followers' ? 'fa-users-slash' : 'fa-user-slash'}`}></i>
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-bold text-white uppercase tracking-wider">
                {searchTerm 
                  ? 'Nenhum usuário encontrado' 
                  : activeTab === 'followers' 
                    ? 'Nenhum seguidor ainda' 
                    : 'Nenhum usuário seguido ainda'}
              </h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                {searchTerm 
                  ? `Não encontramos nenhum resultado para "${searchTerm}". Tente outro termo.`
                  : activeTab === 'followers'
                    ? isOwnProfile 
                      ? 'Compartilhe seu perfil com outros duelistas e colecionadores para ganhar seguidores!'
                      : `@${profileUser?.username || 'Este usuário'} ainda não tem seguidores.`
                    : isOwnProfile
                      ? 'Você ainda não está seguindo ninguém. Explore a comunidade para encontrar duelistas, lojas e amigos!'
                      : `@${profileUser?.username || 'Este usuário'} ainda não segue nenhum jogador.`}
              </p>
            </div>
            {searchTerm ? (
              <button
                onClick={() => setSearchTerm('')}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-colors cursor-pointer"
              >
                Limpar Busca
              </button>
            ) : isOwnProfile && activeTab === 'following' ? (
              <button
                onClick={() => navigate('/comunidade')}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md active:scale-95 cursor-pointer inline-flex items-center gap-2"
              >
                <i className="fas fa-compass"></i>
                <span>Explorar Comunidade</span>
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
